import * as THREE from 'three';
import { ARCHETYPES, buildAllMeshes } from '../art/models';
import type { MeshData } from '../art/shapes';
import { buildPaletteAtlas, makeHullMaterial, makeOutlineMaterial } from '../materials/hullMaterial';

/* ═══════════════════════════════════════════════════════════════════════════
   La flotte — un `InstancedMesh` par silhouette, un seul matériau pour tous.

   Le compromis, posé explicitement : vingt-six archétypes coûtent vingt-six
   draw calls quand ils sont tous à l'écran (ils ne le sont jamais : trois à
   cinq en pratique), contre DEUX programmes de shader pour toute la flotte.
   Sur un shmup, c'est le bon sens de l'échange — le CPU de rendu n'est jamais
   le goulot (§1.1), le nombre de permutations de shaders l'est au démarrage
   (§4.8).

   ⚠️ Deux et pas un, et le second n'est pas un oubli : `USE_INSTANCING` est un
   `#define`. Le matériau de coque sert à la fois aux lots instanciés et au
   vaisseau joueur, qui est un `Mesh` ordinaire — deux jeux de defines, donc
   deux programmes. Les deux sont présents dans la scène de warm-up, donc
   compilés avant la première vague ; c'est tout ce qui compte. En revanche un
   `InstancedMesh` SANS couleur d'instance en produirait un TROISIÈME, ce qui
   explique le tampon neutre posé sur les drones.

   ⚠️ ZÉRO ALLOCATION PAR FRAME. Chaque lot est alloué UNE fois à la capacité
   maximale d'ennemis simultanés ; seul `count` varie. Dimensionner par
   archétype aurait économisé 300 Ko et introduit le pire bug possible dans ce
   genre de jeu : un ennemi qui dépasse la capacité de son lot n'est pas dessiné
   — mais il vit, il tire, et il tue. Un ennemi invisible et mortel n'est pas un
   défaut de rendu, c'est une partie perdue sans explication.
   ═══════════════════════════════════════════════════════════════════════════ */

const _mat = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();

function toGeometry(m: MeshData): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(m.position, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(m.normal, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(m.uv, 2));
  // Le frustum culling est désactivé sur les lots ; la bounding sphere ne sert
  // qu'aux outils de mesure, mais la calculer ici coûte une fois et évite un
  // `null` surprenant au débogage.
  g.computeBoundingSphere();
  return g;
}

export class HullFleet {
  readonly atlas: THREE.DataTexture;
  readonly material: THREE.MeshStandardMaterial;
  private readonly outlineMaterial: THREE.ShaderMaterial;
  private readonly meshes: THREE.InstancedMesh[] = [];
  /** Contours par coque inversée, indexés comme `meshes` ; `null` là où
   *  l'archétype n'y a pas droit. */
  private readonly outlines: (THREE.InstancedMesh | null)[] = [];
  private readonly cursor: Int32Array;

  constructor(scene: THREE.Scene, capacity: number, renderOrder: number) {
    this.atlas = buildPaletteAtlas();
    this.material = makeHullMaterial(this.atlas);
    this.outlineMaterial = makeOutlineMaterial();
    this.cursor = new Int32Array(ARCHETYPES.length);

    const geos = buildAllMeshes().map(toGeometry);

    for (let i = 0; i < ARCHETYPES.length; i++) {
      const geo = geos[i]!;
      const mesh = new THREE.InstancedMesh(geo, this.material, capacity);
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.renderOrder = renderOrder;
      mesh.count = 0;
      // Une couleur d'instance est allouée d'emblée sur TOUS les lots : la
      // créer à la volée au premier ennemi entamé changerait les `#define` du
      // programme et déclencherait une recompilation en plein combat.
      mesh.instanceColor = new THREE.InstancedBufferAttribute(
        new Float32Array(capacity * 3).fill(1),
        3,
      );
      mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
      scene.add(mesh);
      this.meshes.push(mesh);

      if (ARCHETYPES[i]!.outlined === true) {
        const out = new THREE.InstancedMesh(geo, this.outlineMaterial, capacity);
        out.frustumCulled = false;
        out.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        // Juste SOUS la coque : le contour doit être dessiné avant elle, sinon
        // il la recouvre au lieu de la border.
        out.renderOrder = renderOrder - 1;
        out.count = 0;
        scene.add(out);
        this.outlines.push(out);
      } else {
        this.outlines.push(null);
      }
    }
  }

  get archetypeCount(): number {
    return this.meshes.length;
  }

  begin(): void {
    this.cursor.fill(0);
  }

  /** Pose une instance. `scale` est le rayon visuel en unités monde : les
   *  géométries sont normalisées au rayon 1, donc l'échelle EST le rayon —
   *  aucune constante magique ne s'intercale entre la hurtbox et le dessin. */
  add(archetype: number, x: number, y: number, scale: number, r: number, g: number, b: number): void {
    const mesh = this.meshes[archetype];
    if (!mesh) return;
    const n = this.cursor[archetype]!;
    if (n >= mesh.instanceMatrix.count) return;

    _pos.set(x, y, 0);
    _quat.identity();
    _scale.setScalar(scale);
    _mat.compose(_pos, _quat, _scale);
    mesh.setMatrixAt(n, _mat);

    const col = mesh.instanceColor!;
    col.array[n * 3] = r;
    col.array[n * 3 + 1] = g;
    col.array[n * 3 + 2] = b;

    this.cursor[archetype] = n + 1;
  }

  end(): void {
    for (let i = 0; i < this.meshes.length; i++) {
      const mesh = this.meshes[i]!;
      const n = this.cursor[i]!;
      mesh.count = n;
      if (n > 0) {
        /* ⚠️ On ne remonte au GPU que la portion RÉELLEMENT écrite. Sans ces
           plages, three réenvoie le tampon entier — 16 Ko par archétype et par
           frame, pour trois instances utiles. Le coût est invisible au
           profileur CPU et bien réel sur le bus mémoire d'un GPU intégré.

           Les plages sont VIDÉES avant d'être posées : elles s'accumulent
           sinon, et l'optimisation se transforme en fuite silencieuse. */
        mesh.instanceMatrix.clearUpdateRanges();
        mesh.instanceMatrix.addUpdateRange(0, n * 16);
        mesh.instanceMatrix.needsUpdate = true;
        const col = mesh.instanceColor!;
        col.clearUpdateRanges();
        col.addUpdateRange(0, n * 3);
        col.needsUpdate = true;
      }
      const out = this.outlines[i];
      if (out) {
        out.count = n;
        if (n > 0) {
          // Le contour partage la géométrie mais pas les matrices : on les
          // recopie. `copyArray` reste dans le tampon déjà alloué.
          out.instanceMatrix.array.set(mesh.instanceMatrix.array.subarray(0, n * 16));
          out.instanceMatrix.clearUpdateRanges();
          out.instanceMatrix.addUpdateRange(0, n * 16);
          out.instanceMatrix.needsUpdate = true;
        }
      }
    }
  }

  /**
   * Une instance hors champ dans CHAQUE lot, le temps du warm-up.
   *
   * ⚠️ Un `InstancedMesh` à `count = 0` est purement ignoré par le renderer :
   * aucun programme n'est compilé, le warm-up annonce un chiffre rassurant, et
   * les shaders se compilent pendant la première vague. Ici tous les lots
   * partagent un matériau, donc un seul suffirait en théorie — on les remplit
   * tous quand même, parce que « en théorie » est exactement ce qui casse le
   * jour où quelqu'un ajoute une variante.
   */
  primeForWarmup(): void {
    const FAR = 100000;
    this.begin();
    for (let i = 0; i < this.meshes.length; i++) {
      this.add(i, FAR, FAR, 1, 1, 1, 1);
    }
    this.end();
  }
}
